// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Brush State Hook (Refactor 3)
// ═══════════════════════════════════════════════════════════════
// Consolidates all brush-related state: size, opacity, hardness,
// tip, pressure targets, dynamics (jitter, scatter, flow, spacing),
// and syncs to the engine bridge via wp().
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from "react";
import { TIP_NAMES } from "../engine/constants.js";

export function useBrushState(bridgeRef) {
  // Internal wp helper — safe to call before bridge is initialized
  const wp = (n, v) => bridgeRef.current?.setParam(n, v);
  // Core brush params
  const [bsz, setBsz] = useState(12);
  const [bop, setBop] = useState(1);
  const [bhard, setBhard] = useState(0.8);
  const [btip, setBtip] = useState("round");

  // Pressure targets
  const [pressureSize, setPressureSize_] = useState(true);
  const pressureSizeRef = useRef(true);
  const setPressureSize = v => { setPressureSize_(v); pressureSizeRef.current = v; };

  const [pressureOpacity, setPressureOpacity_] = useState(false);
  const pressureOpacityRef = useRef(false);
  const setPressureOpacity = v => { setPressureOpacity_(v); pressureOpacityRef.current = v; };

  const [pressureHardness, setPressureHardness] = useState(false);
  const [pressureFlow, setPressureFlow] = useState(false);
  const [pressureCurve, setPressureCurve] = useState(1);

  // Dynamics (Phase 3)
  const [brushSpacing, setBrushSpacing] = useState(10);
  const [brushFlow, setBrushFlow] = useState(1);
  const [sizeJitter, setSizeJitter] = useState(0);
  const [angleJitter, setAngleJitter] = useState(0);
  const [opacityJitter, setOpacityJitter] = useState(0);
  const [scatterAmount, setScatterAmount] = useState(0);
  const [brushSmoothing, setBrushSmoothing] = useState(0);

  // Color (managed externally but synced here)
  const [bcol, setBcol] = useState("#841116");
  const [bgCol, setBgCol] = useState("#ffffff");
  const bcolRef = useRef("#841116");

  // Hex color → RGB helper
  const hx = (hex) => {
    const h = hex.replace("#", "");
    return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
  };

  // Sync all brush params to engine bridge
  useEffect(() => {
    if (!wp) return;
    const { r, g, b } = hx(bcol);
    wp("brushR", r); wp("brushG", g); wp("brushB", b);
    wp("brushSize", bsz); wp("brushOpacity", bop); wp("brushHardness", bhard);
    wp("brushTip", TIP_NAMES.indexOf(btip));
    wp("pressureSize", pressureSize ? 1 : 0);
    wp("pressureOpacity", pressureOpacity ? 1 : 0);
    // Phase 3 dynamics
    wp("brushSpacing", brushSpacing); wp("brushFlow", brushFlow);
    wp("sizeJitter", sizeJitter); wp("angleJitter", angleJitter);
    wp("opacityJitter", opacityJitter); wp("scatterAmount", scatterAmount);
    wp("brushSmoothing", brushSmoothing);
    wp("pressureHardness", pressureHardness ? 1 : 0);
    wp("pressureFlow", pressureFlow ? 1 : 0);
    wp("pressureCurve", pressureCurve);
    bcolRef.current = bcol;
  }, [bcol, bsz, bop, bhard, btip, pressureSize, pressureOpacity,
      brushSpacing, brushFlow, sizeJitter, angleJitter, opacityJitter,
      scatterAmount, brushSmoothing, pressureHardness, pressureFlow,
      pressureCurve, bridgeRef]);

  return {
    // Core
    bsz, setBsz, bop, setBop, bhard, setBhard, btip, setBtip,
    // Colors
    bcol, setBcol, bgCol, setBgCol, bcolRef,
    // Pressure
    pressureSize, setPressureSize, pressureOpacity, setPressureOpacity,
    pressureHardness, setPressureHardness, pressureFlow, setPressureFlow,
    pressureCurve, setPressureCurve,
    // Dynamics
    brushSpacing, setBrushSpacing, brushFlow, setBrushFlow,
    sizeJitter, setSizeJitter, angleJitter, setAngleJitter,
    opacityJitter, setOpacityJitter, scatterAmount, setScatterAmount,
    brushSmoothing, setBrushSmoothing,
  };
}
